import { useCallback, useEffect, useMemo, useState } from 'react';

import { getCampaignReplaceVariables } from 'services/MailingService';
import { transformVariablesToMergeTags } from '../utils';

/**
 * Hook to load and transform campaign variables from the backend
 * to use as mergeTags in the Topol editor.
 *
 * @param {Object} options
 * @param {boolean} options.isOpen - Whether the editor is open
 * @returns {Object} { mergeTags, loading, error, retry }
 */
export const useTopolVariables = ({ isOpen }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rawVariables, setRawVariables] = useState(null);

    const fetchVariables = useCallback(() => {
        if (!isOpen) return;

        setLoading(true);
        setError(null);

        getCampaignReplaceVariables()
            .then((data) => {
                setRawVariables(data);
            })
            .catch((err) => {
                console.error('Error loading campaign replace variables:', err);
                setError(err);
            })
            .finally(() => setLoading(false));
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && !rawVariables && !error) {
            fetchVariables();
        }
    }, [isOpen, rawVariables, error, fetchVariables]);

    const variables = useMemo(() => {
        if (!rawVariables) return null;
        return transformVariablesToMergeTags(rawVariables);
    }, [rawVariables]);

    const retry = useCallback(() => {
        setError(null);
        fetchVariables();
    }, [fetchVariables]);

    return {
        variables,
        loading,
        error,
        retry,
    };
};

export default useTopolVariables;
